import { Injectable } from '@angular/core';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import { Database } from '@despesse/core';

/** Typed Supabase client for data access, edge functions, and storage. */
@Injectable({ providedIn: 'root' })
export class SupabaseService {
  readonly client: SupabaseClient<Database>;

  constructor() {
    this.client = createClient<Database>(
      environment.supabaseUrl,
      environment.supabasePublishableKey,
      {
        auth: {
          storage: localStorage,
          persistSession: true,
          autoRefreshToken: true,
        },
      },
    );
  }

  /** Accesses a public Supabase table by name. */
  from<T extends keyof Database['public']['Tables']>(table: T) {
    return this.client.from(table);
  }

  /** Invokes a Supabase edge function with a JSON body. */
  async invoke<T>(functionName: string, body: Record<string, unknown> = {}): Promise<T> {
    const { data, error } = await this.client.functions.invoke<T>(functionName, { body });
    if (error) {
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === 'function') {
        try {
          const payload = (await context.clone().json()) as {
            error?: string;
            message?: string;
          };
          const detail = [payload.error, payload.message].filter(Boolean).join(': ');
          if (detail) {
            throw new Error(detail);
          }
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== error.message) {
            throw parseErr;
          }
        }
      }
      throw error;
    }
    return data as T;
  }

  /** Invokes a Supabase edge function with FormData. */
  async invokeForm<T>(functionName: string, formData: FormData): Promise<T> {
    const { data, error } = await this.client.functions.invoke<T>(functionName, { body: formData });
    if (error) {
      const context = (error as { context?: Response }).context;
      let detail = '';
      if (context && typeof context.json === 'function') {
        try {
          const body = (await context.clone().json()) as { error?: string; message?: string };
          detail = [body.error, body.message].filter(Boolean).join(': ');
          if (detail) {
            console.error(`invokeForm ${functionName} failed`, detail, body);
          }
        } catch {
          // ignore body parse failures
        }
      }
      if (detail) {
        throw new Error(detail);
      }
      throw error;
    }
    return data as T;
  }

  /** Accesses a Supabase storage bucket. */
  storage(bucket: string) {
    return this.client.storage.from(bucket);
  }

  /** Access to the Supabase authentication module. */
  get auth() {
    return this.client.auth;
  }
}
